/**
 * Single source of truth for the brokerage deployment.
 *
 * Onboarding WRITES this. The board READS it and enforces it.
 * Nothing in either UI should hard-code a branch, a rung, a threshold, or a
 * permission — if the board behaves differently from what onboarding promised,
 * that is a bug in one of the two, not a difference of opinion.
 *
 * ⛔ IT IS A ROW PER BROKERAGE NOW — `brokerages.config.board`, written by
 * POST /api/broker/config. This file used to say "in production this is a row per
 * brokerage; here it is an in-memory store", and that was the whole of it: the
 * team, the branches, the scope, the escalation ladder, every vetting rule and the
 * TMS mode lived in a module variable and were gone on reload. A brokerage that
 * finished onboarding was un-set-up again after a refresh.
 *
 * The in-memory copy stays as the synchronous read the board renders from, with a
 * subscribe() so it re-renders when onboarding finishes in the same session, plus a
 * draft slot so onboarding can be resumed. `hydrate()` fills it from the server and
 * `commitConfig` pushes to the server — see the note on each.
 *
 * ⛔ `live` IS NOT PART OF WHAT IS SAVED HERE. It is a column with its own route,
 * because an edit to a phone number must not switch outbound calling on.
 */

export const BRANCHES = ["Houston", "Lafayette", "Dallas"];
export const EQUIPMENT = ["Dry van", "Reefer", "Flatbed"];
export const PERMS = [
  ["cover", "Cover and dispatch"],
  ["vetting", "Approve carriers"],
  ["money", "Money and quick pay"],
  ["claims", "Claims and incidents"],
];
export const NOTIFY = ["Phone call", "Slack", "Email"];

/** What the board is allowed to do, by TMS connection mode. */
export const TMS_MODES = {
  watch: { label: "Watch mode", canCall: false, canWrite: false, note: "Alfred shows what it would do. It places no calls and writes nothing." },
  read: { label: "Read only", canCall: true, canWrite: false, note: "Alfred calls and captures, but writes nothing back to the TMS." },
  full: { label: "Read and write", canCall: true, canWrite: true, note: "Alfred works the board and posts what it learns onto the order." },
};

export const DEFAULT_CONFIG = {
  live: false, // the master switch — Alfred is dark until this is on
  tms: { id: "powerbroker", name: "McLeod PowerBroker", env: "Production", mode: "read", connected: false, keyMask: "", lastError: null },
  broker: {
    legalName: "Gulf Bridge Logistics LLC", dba: "Gulf Bridge Freight", mc: "MC-1094882", usdot: "3902114",
    verified: false, callNumber: "", attestation: null, cnam: "pending", callerId: "", officePhone: "", zone: "Central", coi: "",
  },
  scope: {
    branches: ["Houston"],
    mode: "equipment",              // all | equipment | customer
    equipment: ["Dry van"],
    customers: [],
    excludeCustomers: [],
    myLoadsOnly: false,
    goLiveFrom: "new loads only",   // new loads only | everything on the board
    inbound: true,                  // answer the carrier line after hours
    vetAllCarriers: true,           // verification runs outside the calling scope
  },
  team: [],                          // { id, name, role, branch, phone, hours, afterHours, ooo, notify, can[] }
  ladder: [{ who: "rep", at: 0 }, { who: "second", at: 20 }, { who: "manager", at: 45 }],
  rules: {
    fmcsaCallback: true, recordChange: true, voip: true, oos: true, reactivation: true, doubleBroker: true,
    minAge: "90 days", safety: "Satisfactory or unrated", maxOos: "15%",
    autoLiability: "$1,000,000", cargo: "$100,000", breakdown: "$100,000", interchange: "$25,000",
    insWindow: "14 days", insCadence: "Daily", insMidLoad: "Finish the load, block new bookings",
    service: "Highway", requirePacket: true, honorDnu: true, dnuOverride: "Branch manager only, with a reason",
    ceiling: true,
    ceilingBy: { "Dry van": "rep sets per load", Reefer: "rep sets per load", Flatbed: "rep sets per load" },
    killSwitch: [],                 // MCs Alfred may never call again, at the brokerage level
  },
};

/* ------------------------------- the store --------------------------------- */

let current = null;      // null until onboarding completes or hydrate() finds a saved one
let draft = null;        // partial config, for resume
let saver = null;        // (board) => Promise, injected so this module does no fetching itself
let liveSaver = null;    // (live) => Promise — `live` is a COLUMN, with its own route
let lastSaveError = null;
const subs = new Set();

const clone = (o) => JSON.parse(JSON.stringify(o));

export const getConfig = () => (current ? clone(current) : null);
export const getDraft = () => (draft ? clone(draft) : null);
export const getSaveError = () => lastSaveError;

/**
 * ⛔ WHAT A STORED CONFIG IS ALLOWED TO DO TO THE BOARD: nothing it could not do fresh.
 *
 * The board dereferences some of these without guarding — `TMS_MODES[cfg.tms.mode].canCall`
 * is a TypeError for any value that is not watch/read/full, which is a white screen served
 * from whatever is in the database. The backend validates on the way in; this validates on the
 * way OUT as well, because a row written before that validation existed, or by hand, must not
 * be able to take the board down. Anything unrecognised falls back to the DEFAULT_CONFIG value
 * rather than being kept.
 */
const TMS_MODE_VALUES = ['watch', 'read', 'full'];
const SCOPE_MODE_VALUES = ['all', 'equipment', 'customer'];
const PERM_VALUES = PERMS.map(([p]) => p);
const arr = (v, fallback) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : fallback);

export function normaliseConfig(raw) {
  const r = (raw && typeof raw === 'object' && !Array.isArray(raw)) ? raw : {};
  const d = DEFAULT_CONFIG;
  const sec = (k) => ((r[k] && typeof r[k] === 'object' && !Array.isArray(r[k])) ? r[k] : {});
  const tms = sec('tms'); const scope = sec('scope'); const rules = sec('rules');
  return {
    // ⛔ never from the stored blob — `live` is a column, and the board's own switch sets it
    live: false,
    tms: { ...d.tms, ...tms, mode: TMS_MODE_VALUES.includes(tms.mode) ? tms.mode : d.tms.mode },
    broker: { ...d.broker, ...sec('broker') },
    scope: {
      ...d.scope, ...scope,
      mode: SCOPE_MODE_VALUES.includes(scope.mode) ? scope.mode : d.scope.mode,
      branches: arr(scope.branches, d.scope.branches),
      equipment: arr(scope.equipment, d.scope.equipment),
      customers: arr(scope.customers, d.scope.customers),
      excludeCustomers: arr(scope.excludeCustomers, d.scope.excludeCustomers),
    },
    team: (Array.isArray(r.team) ? r.team : [])
      .filter((m) => m && typeof m === 'object' && typeof m.id === 'string' && m.id.trim())
      .map((m) => ({
        ...m,
        // ⛔ `name` IS DEREFERENCED UNGUARDED — the seat picker does `p.name.split(" ")[0]`,
        // which is a TypeError and a white screen for a stored member that has an id and no
        // name. The validation written to stop a stored row taking the board down permitted
        // the most likely stored row.
        name: typeof m.name === 'string' && m.name.trim() ? m.name : 'Unnamed seat',
        role: typeof m.role === 'string' ? m.role : '',
        branch: typeof m.branch === 'string' ? m.branch : '',
        // ⛔ AND `ooo` GATES AUTHORITY. Any truthy value removes a person from the seat picker,
        // from `seatIds`, from `canApprove` and from every ladder rung — so a stored `"false"`
        // silently strips a vetting approver and the board then states that nobody can clear a
        // handoff. A string is not a boolean.
        ooo: m.ooo === true || m.ooo === 'true',
        onHours: m.onHours === undefined ? true : (m.onHours === true || m.onHours === 'true'),
        can: (Array.isArray(m.can) ? m.can : []).filter((c) => PERM_VALUES.includes(c)),
      })),
    // ⛔ `who` IS EITHER A ROLE OR A TEAM MEMBER'S ID. Onboarding's rung picker offers one
    // option per team member with `value={t.id}`, and `ladderFor` resolves it with
    // `team.find(t => t.id === rung.who)` — so a whitelist of role names drops exactly the
    // rung an operator configured by name.
    ladder: (Array.isArray(r.ladder) ? r.ladder : d.ladder)
      .filter((x) => x && typeof x === 'object' && typeof x.who === 'string' && x.who.trim() && Number.isFinite(x.at))
      .sort((a, b) => a.at - b.at),
    rules: { ...d.rules, ...rules,
      killSwitch: arr(rules.killSwitch, d.rules.killSwitch),
      ceilingBy: (rules.ceilingBy && typeof rules.ceilingBy === 'object' && !Array.isArray(rules.ceilingBy))
        ? rules.ceilingBy : d.rules.ceilingBy },
  };
}

/**
 * Give the store a way to save, and fill it from what the server already has.
 *
 * ⛔ THE FETCH IS INJECTED, not done here. This module is imported by the render tests and by
 * the board's module-level code; reaching for the network at import time would make both
 * depend on a server. The app wires it once, in App.jsx.
 */
export function attachSaver(fn, { live } = {}) {
  saver = typeof fn === 'function' ? fn : null;
  liveSaver = typeof live === 'function' ? live : null;
}

/**
 * Turn Alfred on or off for the whole brokerage.
 *
 * ⛔ THIS IS A COLUMN WITH ITS OWN ROUTE, AND IT WAS WIRED TO NOTHING. `setLive` on the board
 * called `commitConfig({ ...cfg, live: v })`; `commitConfig` strips `live` before saving and
 * `normaliseConfig` hard-sets it false on the way back in — so the master switch for calling
 * this brokerage's carriers wrote nothing, read nothing, and reset to "Dark" on every reload,
 * while every other setting now persists. That is the inference an operator would draw, and
 * it would be wrong in the direction that matters. It also triggered a full config save as a
 * side effect, which is the exact coupling the separate route exists to avoid.
 */
export function setLive(v) {
  const on = !!v;
  if (!current) return null;
  current = { ...current, live: on };
  subs.forEach((f) => f(getConfig()));
  if (liveSaver) {
    lastSaveError = null;
    Promise.resolve(liveSaver(on))
      .then(() => { lastSaveError = null; })
      .catch((err) => {
        // ⛔ AND IT FAILS CLOSED. If the server did not record it, the board must not keep
        // showing "Live" — that is a claim that Alfred is calling.
        current = { ...current, live: false };
        lastSaveError = `Alfred was not turned ${on ? 'on' : 'off'} — ${err?.message || 'the write failed'}. The brokerage is still dark.`;
        subs.forEach((f) => f(getConfig()));
      });
  }
  return getConfig();
}

/** Adopt a config the server already had. Returns the config, or null if there was none. */
export function hydrate(board, { live = false } = {}) {
  if (!board || typeof board !== 'object' || Array.isArray(board) || !Object.keys(board).length) return null;
  // ⛔ `live` comes from the COLUMN the caller read, never from the stored blob.
  current = { ...normaliseConfig(board), live: !!live };
  draft = null;
  subs.forEach((f) => f(getConfig()));
  return getConfig();
}

export function saveDraft(partial) {
  draft = clone({ ...(draft || DEFAULT_CONFIG), ...partial });
  return getDraft();
}
/**
 * Commit a config and push it to the server.
 *
 * ⛔ THE LOCAL COMMIT IS NOT CONDITIONAL ON THE SAVE. Onboarding's last step hands the board a
 * config and navigates; making that wait on a round trip would leave the operator on a dead
 * screen when the network hiccups. But a failed save is recorded, because "it saved" is what
 * the absence of a message means — and this config decides who may approve a carrier.
 *
 * ⛔ `save: false` IS WHAT STOPS THE BOARD OVERWRITING A REAL CONFIG WITH ITS DEMO ONE.
 *
 * `Board.jsx` publishes `DEMO_CONFIG` from its `useState` initialiser whenever the store is
 * empty — which it is for the whole of a sign-in, because the magic link opens a new document
 * with an empty sessionStorage and the first `GET /api/broker` goes out unauthenticated. The
 * board then mounted with the saver already attached and POSTed the demo over the brokerage's
 * real team, their permissions, branches, scope, ladder and vetting rules. HTTP 200, nothing
 * on screen. A placeholder the board invented for itself is not a fact about the brokerage,
 * so it is never sent; only a human action saves.
 *
 * `live` is stripped before sending: it is a column with its own route.
 */
export function commitConfig(cfg, { save = true } = {}) {
  current = clone(cfg);
  draft = null;
  subs.forEach((f) => f(getConfig()));
  if (saver && save) {
    const board = { ...current };
    delete board.live;   // a column with its own route; never part of this save
    lastSaveError = null;
    Promise.resolve(saver(board))
      .then(() => { lastSaveError = null; })
      .catch((err) => {
        lastSaveError = err?.message || 'The configuration was not saved.';
        subs.forEach((f) => f(getConfig()));
      });
  }
  return getConfig();
}
export function subscribe(fn) {
  subs.add(fn);
  return () => subs.delete(fn);
}

/* --------------------------- derived, shared logic ------------------------- */

/** Does this load fall inside what onboarding said Alfred may touch? */
export function inScope(load, cfg, seatId) {
  const s = cfg.scope;
  // ⛔ A LOAD WITH NO BRANCH IS A LEGAL ROW — `broker_loads.branch` is nullable and the create
  // sheet does not require one. `${null} is outside Alfred's scope` put the word "null" on
  // screen as the reason a load was skipped, which tells a rep nothing and reads as a bug.
  if (!s.branches.includes(load.branch)) {
    return { ok: false, why: load.branch ? `${load.branch} is outside Alfred's scope` : 'no branch set on this load' };
  }
  if (s.excludeCustomers.includes(load.customerId)) return { ok: false, why: "customer excluded" };
  if (s.myLoadsOnly && seatId && load.rep !== seatId) return { ok: false, why: "not your load" };
  if (s.mode === "equipment") {
    const eq = { van: "Dry van", reefer: "Reefer", flatbed: "Flatbed" }[load.freight.equipment] || load.freight.equipment;
    // ⛔ A LOAD WITH NO EQUIPMENT IS A LEGAL ROW — `buildPlan`'s own comment says so, and the
    // create sheet does not require one — so `${eq}` put the word "undefined" on screen as the
    // reason a load was skipped. Identical to the branch check above, same remedy.
    if (!s.equipment.includes(eq)) {
      return { ok: false, why: eq ? `${eq} is outside Alfred's scope` : "no equipment set on this load" };
    }
  }
  if (s.mode === "customer" && !s.customers.includes(load.customerId)) return { ok: false, why: "customer outside scope" };
  return { ok: true };
}

/** Everyone on the ladder for a load, in order, skipping anyone out of office. */
export function ladderFor(load, cfg) {
  const find = (id) => cfg.team.find((t) => t.id === id) || null;
  return cfg.ladder.map((rung) => {
    let person = null;
    if (rung.who === "rep") person = find(load.rep);
    else if (rung.who === "second") person = find(load.second);
    else if (rung.who === "manager") person = cfg.team.find((t) => t.role === "Branch manager") || null;
    else person = find(rung.who);
    const skipped = person && person.ooo;
    return { at: rung.at, person, skipped };
  });
}

/** Which rung is ringing right now, accounting for OOO and off-shift. */
export function currentRung(load, cfg, waitingMins) {
  const rungs = ladderFor(load, cfg);
  let last = null;
  rungs.forEach((r) => { if (waitingMins >= r.at && r.person && !r.skipped) last = r; });
  return last || rungs.find((r) => r.person && !r.skipped) || null;
}

/** Is there anybody at all who can clear this kind of handoff? */
export function canApprove(cfg, perm) {
  return cfg.team.some((t) => t.can.includes(perm) && !t.ooo);
}

/** Structured outcomes, so "resolved" means something in analytics later. */
export const RESOLVE_OUTCOMES = {
  fraud: ["Confirmed fraudulent — carrier blocked", "Verified by phone — carrier cleared", "Could not verify — load re-posted", "False positive — record was stale"],
  claim: ["Claim opened with the carrier's insurer", "No damage — load continued", "Customer notified, awaiting inspection", "Escalated to legal"],
  falloff: ["Recovered with another carrier", "Load re-posted", "Customer notified and appointment moved", "Load cancelled — TONU filed"],
  rate: ["Ceiling raised, carrier booked", "Held the ceiling, booked another carrier", "Walked away from the carrier", "Customer rate renegotiated"],
  money: ["Approved and paid", "Approved, billing to customer", "Declined — carrier absorbed", "Deferred to invoice audit"],
  appointment: ["Appointment moved", "Held the original window", "Relay arranged", "Customer accepted a late delivery"],
  tracking: ["Reached the driver — position confirmed", "Reached dispatch — position confirmed", "Escalated to the carrier's after-hours line", "No contact — load flagged at risk"],
  unreachable: ["Reached on a later attempt", "Reached through dispatch", "Carrier unresponsive — load re-covered", "Emergency contact used"],
};
